/**
 * Per-process FAQ hooks — public landing FAQ + tenant-admin editor.
 *
 * Public hook (`usePublicProcessFaq`) drives the "Časté dotazy"
 * section on the landing / `/jak-funguje` pages. It fetches one
 * sekce-per-aktivovaný-modul, server-rendered, already filtered to
 * the active tenant's `service_capabilities`.
 *
 * Tenant-admin hooks back the „Public web" tab ve skill matrix (override per
 * (capability_key, lang)); platform hooks back the „Public web" tab na
 * AdminPlatformProcesses (edit platform defaults = org_id NULL řádky).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

const API = "/api/v1";
const ADMIN_BASE = `${API}/admin/process-faq`;

export interface ProcessFaqItem {
  q: string;
  a: string;
}

export interface ProcessFaqEntry {
  capability_key: string;
  display_name: string;
  items: ProcessFaqItem[];
}

// --- Public ---------------------------------------------------------

export function usePublicProcessFaq(
  lang: string,
  options?: Omit<UseQueryOptions<ProcessFaqEntry[]>, "queryKey" | "queryFn">,
) {
  return useQuery<ProcessFaqEntry[]>({
    queryKey: ["public", "process-faq", lang],
    queryFn: () =>
      apiFetch<ProcessFaqEntry[]>(
        `${API}/process-faq?lang=${encodeURIComponent(lang)}`,
      ),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

// --- Admin ---------------------------------------------------------

export interface AdminProcessFaqDetail {
  capability_key: string;
  display_name: string;
  lang: string;
  default_items: ProcessFaqItem[];
  override_items: ProcessFaqItem[] | null;
  updated_at: string | null;
  updated_by_user_email: string | null;
}

export interface AdminProcessFaqSavePayload {
  items: ProcessFaqItem[];
}

const adminKeys = {
  list: () => ["admin", "process-faq"] as const,
  detail: (capability: string | null, lang: string | null) =>
    ["admin", "process-faq", capability, lang] as const,
};

export function useAdminProcessFaqDetail(
  capabilityKey: string | null,
  lang: string | null,
) {
  const enabled = capabilityKey !== null && lang !== null;
  return useQuery<AdminProcessFaqDetail>({
    queryKey: adminKeys.detail(capabilityKey, lang),
    queryFn: () =>
      apiFetch<AdminProcessFaqDetail>(
        `${ADMIN_BASE}/${capabilityKey}/${lang}`,
      ),
    enabled,
    staleTime: 5 * 1000,
  });
}

export function useSaveProcessFaq(capabilityKey: string, lang: string) {
  const qc = useQueryClient();
  return useMutation<
    AdminProcessFaqDetail,
    Error,
    AdminProcessFaqSavePayload
  >({
    mutationFn: (body) =>
      apiFetch<AdminProcessFaqDetail>(
        `${ADMIN_BASE}/${capabilityKey}/${lang}`,
        { method: "PUT", body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.list() });
      qc.invalidateQueries({
        queryKey: adminKeys.detail(capabilityKey, lang),
      });
      qc.invalidateQueries({ queryKey: ["public", "process-faq"] });
    },
  });
}

export function useResetProcessFaq(capabilityKey: string, lang: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () =>
      apiFetch<void>(`${ADMIN_BASE}/${capabilityKey}/${lang}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.list() });
      qc.invalidateQueries({
        queryKey: adminKeys.detail(capabilityKey, lang),
      });
      qc.invalidateQueries({ queryKey: ["public", "process-faq"] });
    },
  });
}

// --- Platform (edit platform defaults = org_id NULL rows) ------------

const PLATFORM_BASE = `${API}/admin/platform-process-faq`;

export interface PlatformProcessFaqDetail {
  capability_key: string;
  lang: string;
  items: ProcessFaqItem[];
}

const platformKeys = {
  detail: (capability: string | null, lang: string | null) =>
    ["admin", "platform-process-faq", capability, lang] as const,
};

/** Invalidate everything a default change ripples into: this slot, the tenant
 *  editor's `default_items`, and the public FAQ. */
function invalidatePlatformFaq(
  qc: ReturnType<typeof useQueryClient>,
  capabilityKey: string,
  lang: string,
) {
  qc.invalidateQueries({ queryKey: platformKeys.detail(capabilityKey, lang) });
  qc.invalidateQueries({ queryKey: ["admin", "process-faq"] });
  qc.invalidateQueries({ queryKey: ["public", "process-faq"] });
}

export function usePlatformProcessFaq(
  capabilityKey: string | null,
  lang: string | null,
) {
  const enabled = capabilityKey !== null && lang !== null;
  return useQuery<PlatformProcessFaqDetail>({
    queryKey: platformKeys.detail(capabilityKey, lang),
    queryFn: () =>
      apiFetch<PlatformProcessFaqDetail>(
        `${PLATFORM_BASE}/${capabilityKey}/${lang}`,
      ),
    enabled,
    staleTime: 5 * 1000,
  });
}

export function useSavePlatformProcessFaq(capabilityKey: string, lang: string) {
  const qc = useQueryClient();
  return useMutation<PlatformProcessFaqDetail, Error, AdminProcessFaqSavePayload>(
    {
      mutationFn: (body) =>
        apiFetch<PlatformProcessFaqDetail>(
          `${PLATFORM_BASE}/${capabilityKey}/${lang}`,
          { method: "PUT", body },
        ),
      onSuccess: () => invalidatePlatformFaq(qc, capabilityKey, lang),
    },
  );
}

export function useResetPlatformProcessFaq(capabilityKey: string, lang: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () =>
      apiFetch<void>(`${PLATFORM_BASE}/${capabilityKey}/${lang}`, {
        method: "DELETE",
      }),
    onSuccess: () => invalidatePlatformFaq(qc, capabilityKey, lang),
  });
}
